<template>
  <!--
    장비 설치 신청 목록(설계안 2판 §10.2 · PDF 8쪽) — 사장님이 POS 에서 보낸 신청을 담당자가 단계대로 넘긴다. 구독과 별개 기능이다
    (§9.0): 구독·요금제·체험을 읽지 않는다. 조회는 목록·요약 둘, 쓰기는 단계 넷(연락 완료·설치일·설치 완료·취소)이다. 직원이 매장을
    골라 신청을 넣는 [신청 추가] 창(§9.7)은 `EquipmentRequestAddDialog` 다.
    말·거르기·단계·넘김 표시는 `utils/equipmentRequest` 한 곳 — 화면은 조회·쓰기와 그리기만.
  -->
  <div class="contain" data-testid="equipment-request-page">
    <div class="search_form" ref="search_form">
      <p class="form-field-hint equipment__intro">{{ $t(text.intro) }}</p>

      <div v-if="canSummary" class="equipment-cards" data-testid="equipment-cards">
        <button
          v-for="card in summaryView.cards"
          :key="card.status"
          type="button"
          class="equipment-card"
          :class="{ 'is-active': cardActiveOf(filter, card.status), 'is-lead': card.status === STATUS.NEW }"
          :data-testid="`equipment-card-${card.status}`"
          @click="pickCard(card.status)">
          <span class="equipment-card__label">{{ $t(card.label) }}</span>
          <span class="equipment-card__count">{{ card.countText }}</span>
          <span
            v-if="card.status === STATUS.NEW && summaryView.overdueText"
            class="equipment-card__overdue"
            data-testid="equipment-card-overdue"
            >{{ summaryView.overdueText }}</span
          >
        </button>
      </div>

      <el-form :inline="true" :size="size" label-suffix=":" @submit.native.prevent>
        <el-form-item :label="$t(text.filterStatus)">
          <el-select
            v-model="filter.status"
            data-testid="equipment-filter-status"
            :style="{ width: '130px' }"
            @change="refilter">
            <el-option :label="$t(text.filterAll)" value=""></el-option>
            <el-option
              v-for="option in statusOptions"
              :key="option.value"
              :label="$t(option.label)"
              :value="option.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t(text.filterPeriod)">
          <el-date-picker
            v-model="filter.period"
            type="daterange"
            unlink-panels
            clearable
            value-format="yyyy-MM-dd"
            :range-separator="$t('~')"
            :start-placeholder="$t('시작일')"
            :end-placeholder="$t('종료일')"
            :style="{ width: $device.mobile ? `100%` : `250px` }"
            @change="refilter"></el-date-picker>
        </el-form-item>
        <el-form-item :label="$t(text.filterQuery)">
          <el-input
            v-model="filter.q"
            clearable
            data-testid="equipment-filter-q"
            :placeholder="$t(text.filterQueryPlaceholder)"
            :style="{ width: '200px' }"
            @input="dropInstalledRange"
            @keyup.enter.native="searchFirstPage"></el-input>
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="filter.overdue" data-testid="equipment-filter-overdue" @change="refilter">{{
            $t(text.filterOverdue)
          }}</el-checkbox>
        </el-form-item>
      </el-form>
    </div>

    <div class="buttons" ref="buttons">
      <el-button :size="size" icon="el-icon-search" @click="searchFirstPage">{{ $t('검색') }}</el-button>
      <!-- 직원이 매장을 골라 신청을 넣는다 — POS 신청과 같은 신청·같은 알림톡(설계안 §9.7) -->
      <el-button
        v-if="canAdd"
        :size="size"
        type="primary"
        plain
        icon="el-icon-plus"
        data-testid="equipment-add"
        @click="addVisible = true"
        >{{ $t(text.add) }}</el-button
      >
    </div>

    <EquipmentRequestAddDialog :visible.sync="addVisible" @changed="reload" />

    <el-table
      :height="tableHeight - 48"
      border
      ref="table"
      row-key="requestId"
      :data="tableData"
      :size="size"
      :empty-text="$t(text.empty)"
      :default-sort="{ prop: defaultSort.sortProp, order: defaultSort.sortOrder }"
      @sort-change="handleSortChange">
      <el-table-column type="expand" width="40">
        <template slot-scope="scope">
          <dl class="equipment-detail" data-testid="equipment-detail">
            <dt>{{ $t(text.detailContact) }}</dt>
            <dd>
              <span data-testid="equipment-detail-contact">{{ telText(scope.row.contactTel) || '—' }}</span>
              <span
                v-if="originalTelOf(scope.row)"
                class="equipment-contact__original"
                data-testid="equipment-detail-contact-original"
                >({{ $t(text.originalTel) }} {{ telText(originalTelOf(scope.row)) }})</span
              >
            </dd>
            <dt>{{ $t(text.detailCeo) }}</dt>
            <dd>{{ scope.row.ceoNm || '—' }}</dd>
            <dt>{{ $t(text.detailAddress) }}</dt>
            <dd>{{ scope.row.address || '—' }}</dd>
            <dt>{{ $t(text.detailMemo) }}</dt>
            <dd class="equipment-detail__memo">{{ scope.row.memo || '—' }}</dd>
            <dt>{{ $t(text.detailHistory) }}</dt>
            <dd>
              <ul class="equipment-history">
                <li v-for="entry in historyOf(scope.row)" :key="entry.label">
                  <span class="equipment-history__label">{{ $t(entry.label) }}</span>
                  <span>{{ entry.at }}</span>
                  <span v-if="entry.by" class="equipment-history__by">{{ entry.by }}</span>
                  <span v-if="entry.note" class="equipment-history__note">{{ entry.note }}</span>
                </li>
              </ul>
            </dd>
          </dl>
        </template>
      </el-table-column>
      <!--
        핵심 칸이 앞이다 — 상태·매장·신청 시각·연락처·진행(합 약 700px)이 1024×768 의 표 폭 안에서 가로 스크롤 없이 보인다. 참고 칸
        (주소·메모)만 넘치면 스크롤로 보고, 둘 다 행을 펼치면 전체가 보인다 — 환불 실패 목록(`OrderRefundPendingView`, V3-4)과 같은 규칙.
      -->
      <el-table-column :label="$t(text.columnStatus)" width="96" align="center">
        <template slot-scope="scope">
          <span
            class="equipment-status"
            :class="`equipment-status--${statusViewOf(scope.row).tone}`"
            data-testid="equipment-status"
            >{{ $t(statusViewOf(scope.row).label) }}</span
          >
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnStore)" min-width="130">
        <template slot-scope="scope">
          <div class="equipment-store">{{ scope.row.storeNm }}</div>
          <div class="equipment-store__id">{{ scope.row.storeId }}</div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnRequested)" prop="requestedDtm" width="150" sortable="custom">
        <template slot-scope="scope">
          <span
            :class="{ 'equipment-overdue': requestedViewOf(scope.row, nowMs).overdue }"
            data-testid="equipment-requested"
            >{{ requestedViewOf(scope.row, nowMs).text }}</span
          >
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnContact)" width="120" align="center">
        <template slot-scope="scope">
          <span data-testid="equipment-contact">{{ maskedTel(scope.row.contactTel) || '—' }}</span>
          <!--
            칸 폭(120px)에 한 줄로 들어가지 않아 매장 칸의 S… 처럼 아래에 작게 둔다. 표 칸은 글자 중간에서도 줄을 바꾸므로(Element
            `word-break: break-all`) 번호는 통째로 한 줄에 — 「(원래」 다음에서만 줄이 바뀐다
          -->
          <div
            v-if="originalTelOf(scope.row)"
            class="equipment-contact__original"
            data-testid="equipment-contact-original">
            ({{ $t(text.originalTel) }}
            <span class="equipment-contact__tel">{{ maskedTel(originalTelOf(scope.row)) }})</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnActions)" width="170" align="center">
        <template slot-scope="scope">
          <div class="equipment-actions">
            <el-button
              v-if="actionsOf(scope.row).next"
              size="mini"
              plain
              data-testid="equipment-step"
              :loading="busyId === scope.row.requestId"
              :disabled="busyId !== null && busyId !== scope.row.requestId"
              @click="startStep(scope.row, actionsOf(scope.row).next.step)"
              >{{ $t(actionsOf(scope.row).next.label) }}</el-button
            >
            <el-button
              v-if="actionsOf(scope.row).reschedule"
              type="text"
              size="mini"
              data-testid="equipment-reschedule"
              :disabled="busyId !== null"
              @click="startStep(scope.row, actionsOf(scope.row).reschedule.step)"
              >{{ $t(actionsOf(scope.row).reschedule.label) }}</el-button
            >
            <el-button
              v-if="actionsOf(scope.row).cancel"
              type="text"
              size="mini"
              class="equipment-actions__cancel"
              data-testid="equipment-cancel"
              :disabled="busyId !== null"
              @click="startStep(scope.row, STEP.CANCEL)"
              >{{ $t(text.stepCancel) }}</el-button
            >
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnAddress)" prop="address" min-width="120" show-overflow-tooltip>
      </el-table-column>
      <el-table-column :label="$t(text.columnMemo)" min-width="100" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.memo || '—' }}</template>
      </el-table-column>
    </el-table>

    <list-pagination
      :search-form="searchForm"
      :total="totalElements"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"></list-pagination>

    <el-dialog
      :title="$t(dialog.step === STEP.SCHEDULE ? text.scheduleTitle : text.cancelTitle)"
      :visible.sync="dialog.visible"
      width="400px"
      data-testid="equipment-dialog"
      :close-on-click-modal="false">
      <p class="equipment-dialog__store">{{ dialog.row ? `${dialog.row.storeNm} · ${dialog.row.storeId}` : '' }}</p>
      <el-form label-position="top" @submit.native.prevent>
        <el-form-item v-if="dialog.step === STEP.SCHEDULE" :label="$t(text.scheduleLabel)" :error="dialog.problem">
          <el-date-picker
            v-model="dialog.installDate"
            type="date"
            value-format="yyyy-MM-dd"
            data-testid="equipment-install-date"
            :placeholder="$t(text.scheduleLabel)"
            :style="{ width: '100%' }"></el-date-picker>
        </el-form-item>
        <el-form-item v-else :label="$t(text.cancelLabel)" :error="dialog.problem">
          <el-input
            v-model="dialog.reason"
            type="textarea"
            :rows="3"
            data-testid="equipment-cancel-reason"
            :placeholder="$t(text.cancelPlaceholder)"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :size="size" @click="dialog.visible = false">{{ $t(text.close) }}</el-button>
        <el-button
          :size="size"
          type="primary"
          data-testid="equipment-dialog-submit"
          :loading="busyId !== null"
          @click="submitDialog"
          >{{ $t(text.save) }}</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import EquipmentRequestAddDialog from '@/components/equipment/EquipmentRequestAddDialog.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import pagedListMixin from '@/mixins/pagedListMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import { initData } from '@/utils/constants'
import {
  EQUIPMENT_DEFAULT_SORT,
  EQUIPMENT_REQUEST_ACTION,
  EQUIPMENT_REQUEST_API,
  EQUIPMENT_STATUS,
  EQUIPMENT_STATUS_OPTIONS,
  EQUIPMENT_STEP,
  EQUIPMENT_STEP_REQUEST_CONFIG,
  EQUIPMENT_SUMMARY_API,
  EQUIPMENT_TEXT,
  cardActiveOf,
  cardFilterOf,
  editedFilterOf,
  equipmentListQuery,
  historyOf,
  isStatusChanged,
  maskedTel,
  originalTelOf,
  requestedViewOf,
  rowActionsOf,
  statusViewOf,
  stepDraftOf,
  stepRequestOf,
  summaryCardsOf,
} from '@/utils/equipmentRequest'
import { withPaging } from '@/utils/pagedList'
import { telText } from '@/utils/telNumber'

/** 확인 하나로 넘기는 단계 — 입력이 없는 둘. 설치일·취소는 창에서 값을 받는다 */
const CONFIRM_TEXT = Object.freeze({
  [EQUIPMENT_STEP.CONTACTED]: EQUIPMENT_TEXT.confirmContacted,
  [EQUIPMENT_STEP.INSTALLED]: EQUIPMENT_TEXT.confirmInstalled,
})

/** 닫힌 창 */
const closedDialog = () => ({ visible: false, step: null, row: null, installDate: '', reason: '', problem: '' })

export default {
  name: 'EquipmentRequestListView',

  components: { EquipmentRequestAddDialog },

  mixins: [mainVueMixin, pagedListMixin],

  data: function () {
    var data = initData()
    data.url = EQUIPMENT_REQUEST_API
    data.text = EQUIPMENT_TEXT
    data.STATUS = EQUIPMENT_STATUS
    data.STEP = EQUIPMENT_STEP
    data.statusOptions = EQUIPMENT_STATUS_OPTIONS
    data.defaultSort = EQUIPMENT_DEFAULT_SORT
    data.searchForm = Object.assign(withPaging({}), EQUIPMENT_DEFAULT_SORT)
    /**
     * 거르기 넷 — 상태(빈 글자 = 전체) · 신청일 · 검색어 · 기한 넘김만. 그리고 칸이 없는 설치 완료일(`installed`) — 「이번 달 설치
     * 완료」 카드만 걸고, 사람이 다른 칸을 바꾸면 풀린다(`editedFilterOf`)
     */
    data.filter = { status: '', period: null, q: '', overdue: false, installed: null }
    /** 요약 응답 — 못 읽었으면 `null`(카드는 건수 대신 「–」) */
    data.summary = null
    /** 목록을 받은 때 — 「오늘·어제」를 이 순간으로 말한다(시계를 읽는 자리는 이 화면이다) */
    data.nowMs = Date.now()
    /** 단계 바꾸기 중인 신청 — 그동안 다른 단추를 잠근다 */
    data.busyId = null
    data.dialog = closedDialog()
    /** 직접 추가 창 */
    data.addVisible = false

    return data
  },

  computed: {
    canSummary: function () {
      return this.checkAuthActionAccess(EQUIPMENT_REQUEST_ACTION.SUMMARY)
    },
    /** 직접 추가 — 창이 부르는 둘(미리 채움·추가)을 다 가진 세션만. 하나라도 없으면 누르는 순간 403(= 로그아웃)이다 */
    canAdd: function () {
      return (
        this.checkAuthActionAccess(EQUIPMENT_REQUEST_ACTION.PREFILL) &&
        this.checkAuthActionAccess(EQUIPMENT_REQUEST_ACTION.ADD)
      )
    },
    summaryView: function () {
      return summaryCardsOf(this.summary)
    },
  },

  created: function () {
    this.reload()
  },

  methods: {
    cardActiveOf,
    historyOf,
    maskedTel,
    originalTelOf,
    requestedViewOf,
    statusViewOf,
    telText,

    actionsOf: function (row) {
      return rowActionsOf(row, this.checkAuthActionAccess)
    },

    /** 목록(1페이지)과 요약을 함께 다시 읽는다 — 첫 진입 */
    reload: function () {
      this.searchFirstPage()
      this.loadSummary()
    },

    /** 단계를 바꾼 뒤 — 보던 페이지 그대로 목록과 요약을 다시 읽는다 */
    refresh: function () {
      this.search()
      this.loadSummary()
    },

    search: function () {
      this.mainVue.$axios.search(
        this.url,
        equipmentListQuery(this.filter, this.searchQuery()),
        function (result) {
          this.nowMs = Date.now()
          this.applyPage(result)
        }.bind(this)
      )
    },

    loadSummary: async function () {
      if (!this.canSummary) return

      const outcome = await this.mainVue.$axios.search(
        EQUIPMENT_SUMMARY_API,
        null,
        function (result) {
          this.summary = result
        }.bind(this)
      )

      if (!outcome.ok) this.summary = null
    },

    pickCard: function (status) {
      this.filter = cardFilterOf(this.filter, status, Date.now())
      this.searchFirstPage()
    },

    /** 상태·신청일·기한 넘김을 바꿨다 — 카드가 건 설치 완료일을 풀고 새로 읽는다 */
    refilter: function () {
      this.dropInstalledRange()
      this.searchFirstPage()
    },

    /** 검색어를 고치는 중 — 읽기는 엔터·검색 단추가 한다 */
    dropInstalledRange: function () {
      this.filter = editedFilterOf(this.filter)
    },

    /** 단추를 눌렀다 — 입력이 없는 단계는 확인 하나, 설치일·취소는 창을 연다(설치일 창은 그 행의 설치일을 미리 채운다) */
    startStep: async function (row, step) {
      if (!CONFIRM_TEXT[step]) {
        this.dialog = Object.assign(closedDialog(), { visible: true, step, row }, stepDraftOf(row, step))
        return
      }

      try {
        await this.$confirm(`${row.storeNm} — ${this.$t(CONFIRM_TEXT[step])}`, this.$t('확인'), {
          confirmButtonText: this.$t(this.actionsOf(row).next.label),
          cancelButtonText: this.$t(this.text.close),
          type: 'warning',
        })
      } catch (cancelled) {
        return
      }

      const outcome = await this.runStep(row, step, null)
      if (outcome.failure) this.mainVue.$message({ type: 'error', message: outcome.failure })
    },

    submitDialog: async function () {
      const { row, step } = this.dialog
      const outcome = await this.runStep(row, step, {
        installDate: this.dialog.installDate,
        reason: this.dialog.reason,
      })

      if (outcome.failure) {
        this.dialog.problem = outcome.failure
        return
      }

      this.dialog.visible = false
    },

    /**
     * **단계 바꾸기 한 곳** — 보내고, 결과를 말하고, 다시 읽는다. 답: `{failure}`(사람에게 할 말 — 부르는 쪽이 자리에 맞게
     * 그린다) 또는 `{}`(끝났다 — 바뀌었거나 그 사이 바뀌어 다시 읽었다).
     *
     * 409 `STATUS_CHANGED` 는 실패가 아니다 — 다른 담당자가 먼저 바꿨으니 목록을 다시 읽고 그렇게 말한다(§10.2).
     */
    runStep: async function (row, step, input) {
      const request = stepRequestOf(step, row.requestId, input)
      if (!request.ok) return { failure: this.$t(request.problem) }

      this.busyId = row.requestId
      try {
        await this.$axios.post(request.url, request.body, EQUIPMENT_STEP_REQUEST_CONFIG)
        this.mainVue.$message({ type: 'success', message: this.$t(this.text.stepDone) })
      } catch (error) {
        if (!isStatusChanged(error)) return { failure: httpErrorMessage(error, this.$t(this.text.stepFailed)) }

        this.mainVue.$message({ type: 'warning', message: this.$t(this.text.statusChanged) })
      } finally {
        this.busyId = null
      }

      this.refresh()
      return {}
    },
  },
}
</script>

<style scoped>
.equipment__intro {
  margin: 0 0 12px;
}

.equipment-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.equipment-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-card);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.equipment-card:hover {
  box-shadow: var(--shadow-card-hover);
}

.equipment-card:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.equipment-card.is-lead {
  border-color: var(--color-primary-lighter);
  background: var(--color-primary-light);
}

.equipment-card.is-active {
  border-color: var(--color-primary);
}

.equipment-card__label {
  font-size: 13px;
  color: var(--color-text-regular);
}

.equipment-card__count {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.equipment-card.is-lead .equipment-card__count {
  color: var(--color-primary-dark);
}

.equipment-card__overdue {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-danger);
}

.equipment-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.equipment-status--new {
  background: var(--color-danger-light);
  color: var(--color-danger);
}

.equipment-status--contacted {
  background: var(--color-info-light);
  color: var(--color-info);
}

.equipment-status--scheduled {
  background: var(--color-warning-light);
  color: #b07a00;
}

.equipment-status--installed {
  background: var(--color-success-light);
  color: #389e0d;
}

.equipment-status--canceled,
.equipment-status--unknown {
  background: var(--color-border-light);
  color: var(--color-text-secondary);
}

.equipment-store {
  font-weight: 500;
  color: var(--color-text-primary);
}

.equipment-store__id,
.equipment-contact__original {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.equipment-contact__tel {
  white-space: nowrap;
}

.equipment-overdue {
  font-weight: 500;
  color: var(--color-danger);
}

.equipment-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2px 8px;
}

/* 설치일 확정 행은 단추가 셋이라 둘째 줄로 넘어간다 — Element 의 이웃 단추 왼쪽 여백 대신 gap 이 간격을 맡는다 */
.equipment-actions .el-button + .el-button {
  margin-left: 0;
}

.equipment-actions__cancel {
  color: var(--color-text-secondary);
}

.equipment-detail {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 6px 16px;
  margin: 0;
  padding: 4px 16px;
}

.equipment-detail dt {
  color: var(--color-text-secondary);
}

.equipment-detail dd {
  margin: 0;
  color: var(--color-text-primary);
}

.equipment-detail__memo {
  white-space: pre-wrap;
}

.equipment-detail .equipment-contact__original {
  margin-left: 6px;
}

.equipment-history {
  margin: 0;
  padding: 0;
  list-style: none;
}

.equipment-history li {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.equipment-history__label {
  min-width: 72px;
  font-weight: 500;
}

.equipment-history__by,
.equipment-history__note {
  color: var(--color-text-regular);
}

.equipment-dialog__store {
  margin: 0 0 12px;
  color: var(--color-text-regular);
}

@media (max-width: 768px) {
  .equipment-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
